import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import api from "@/admin/lib/api";
import { scheduleResourceStateRefresh } from "@/shared/lib/schedule-resource-state-refresh";
import { type DisclaimerConfig } from "@/shared/components/claims/disclaimer";
import type { ProductFilterFacet } from "@/shared/components/ui/product-filter-menu";
import type { SortableColumn } from "@/admin/hooks/use-products";
import type { ExceptionActionBinding } from "@/shared/lib/workflows/types";
import type { ImageReview } from "@/admin/components/claims/duplicate-media-panel";

// ── Types ────────────────────────────────────────────────

/** One status bucket from `/claims/claims/status-counts/`. `label` and `color`
 *  come from the tenant's own WorkflowState rows, never from a frontend map. */
export interface ClaimStatusCount {
  key: string;
  label: string;
  color: string;
  count: number;
}

export interface ClaimRow {
  uuid: string;
  /** Tenant-configured readable ID (WP1-12); empty until a scheme assigns one. */
  claim_number?: string;
  holding_uuid: string;
  product_name: string;
  consumer_account_name: string | null;
  consumer_account_email: string | null;
  consumer_account_phone: string | null;
  consumer_account_address: string | null;
  dealer_name: string | null;
  claim_schema_name: string;
  claim_schema_key: string;
  claim_state: string | null;
  claim_state_color: string | null;
  /**
   * An inspection on this claim is still open. Requesting one does not move
   * the claim, so this is what distinguishes "in Lab Analysis" from "in Lab
   * Analysis, waiting on an inspection" (WPS-175 #5).
   */
  has_open_inspection?: boolean;
  /**
   * An escalation on this claim is still open. It pauses the claim - every
   * action is refused until it is resolved - so the list flags it (WPS-175 #6).
   */
  has_open_escalation?: boolean;
  is_flagged: boolean;
  flag_reason: string;
  assigned_to: { uuid: string; name: string } | null;
  /**
   * The owner of the claim's CURRENT stage (Lab Analysis -> the Lab holder, the
   * settlement stages -> the Settlement holder, every other stage -> the
   * Resolution Specialist). Resolved server-side and falling back to
   * `assigned_to`, so it drives the Assignee column showing who is on the claim
   * right now (WPS-160). `assigned_to` stays the primary owner.
   */
  // `uuid` is null for a stage owned by an external party (name "External").
  current_stage_assignee?: { uuid: string | null; name: string } | null;
  /**
   * Distinct reviewers across an umbrella parent's child claims (the parent
   * itself has no assignee). Empty for leaf claims. Drives the grouped avatars
   * shown in the Assignee column for parent rows.
   */
  child_assignees?: { uuid: string; name: string }[];
  // --- Spotlight indicator ---
  // Whether the current user has an unread notification for this claim, plus
  // the newest such notification's message + level. Driven by the notification
  // infrastructure (server-side, per-user), so `active` replaces the old
  // localStorage/needs_action logic and `title` explains what it is about.
  spotlight: {
    active: boolean;
    // True only for a freshly-submitted, not-yet-actioned claim (the
    // "awaiting first decision" notification). Drives the new/unopened row
    // emphasis, distinct from `active` (any unread) which drives the dot.
    is_new: boolean;
    title: string;
    level: "info" | "attention" | "critical";
  };
  attribute_values: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  /** When the claim reached a terminal workflow state; null while still open. */
  closed_at?: string | null;
  /** When the claim entered its current state (drives "time in current status"). */
  status_entered_at?: string | null;
  /** Contractor assigned via the registration; empty string when none. */
  contractor_name?: string | null;
  /** Primary payment method (Check / voucher / ACH); empty when no payment yet. */
  payment_type?: string | null;
  /** The state the claim was in before its current one; empty when none. */
  previous_status?: string | null;
}

/** WPS-145: a claim's automated-messaging switch, as served by the API. */
export interface CommunicationHold {
  /** Attribute code_name to write when toggling. */
  code_name: string
  /** What this tenant calls the hold. */
  label: string
  /** Helper text shown under the switch. */
  description: string
  /** True while automated messaging about this claim is paused. */
  active: boolean
  /** Whether the current user holds the codename that gates the toggle. */
  can_manage: boolean
}

export interface ClaimDetail {
  uuid: string;
  /** Tenant-configured readable ID (WP1-12); empty until a scheme assigns one. */
  claim_number?: string;
  holding_uuid: string;
  product_uuid: string;
  product_name: string;
  product_short_name?: string;
  /** WPS-165: the product LINE (parent product), e.g. "TruDefinition Duration". */
  product_line_name?: string;
  /**
   * WPS-175 #14: the claim property's geographic zone, resolved server-side
   * from the claim's own captured address and only then from the
   * registration's. Empty when the claim carries no address to resolve from.
   */
  geo_zone_name?: string;
  product_type?: string;
  product_template_name?: string;
  product_category_name?: string;
  consumer_name: string;
  consumer_first_name?: string;
  consumer_last_name?: string;
  consumer_email: string;
  consumer_phone: string;
  consumer_address?: unknown;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_registration: string;
  vehicle_colour?: string;
  vehicle_vin?: string;
  host_attributes?: Array<{
    code_name: string;
    display_name: string;
    value: string;
  }>;
  dealer_name: string;
  // The partner this claim concerns (WPS-163): the claim's own override if one
  // is set, otherwise the registration's partner. Same payload shape as a
  // partnership detail, so the claim renders the identical profile panel the
  // registration does. `partner_is_inherited` distinguishes the two.
  partner?: import("@/admin/types/organisations").Partnership | null;
  partner_is_inherited?: boolean;
  warranties?: Array<{
    uuid: string;
    class_name: string;
    warranty_type: string;
    duration: string;
    status: string;
    active_from: string;
    expires_at: string | null;
  }>;
  purchase_price?: string | null;
  purchase_currency?: string;
  assigned_to: { uuid: string; name: string } | null;
  /** WPS-142 #10: owner of the claim's CURRENT stage (e.g. the Lab Analyst while
   *  in Lab Analysis). Resolved server-side; falls back to ``assigned_to`` so it
   *  is always populated. ``assigned_to`` stays the primary owner. */
  // `uuid` is null for a stage owned by an external party (name "External").
  current_stage_assignee?: { uuid: string | null; name: string } | null;
  assigned_at: string | null;
  claim_schema_uuid: string;
  claim_schema_name: string;
  claim_schema_key: string;
  /**
   * WPS-145: the claim's communication hold - the switch that pauses every
   * automated message about this claim so a person owns the correspondence
   * instead. ``null`` when the tenant has not configured one, which is the
   * signal to render nothing. Label and description are tenant vocabulary
   * (Owens Corning call theirs "Legal Handling"), never hardcoded here.
   */
  communication_hold?: CommunicationHold | null;
  is_flagged: boolean;
  flag_reason: string;
  flagged_by_name: string | null;
  flagged_at: string | null;
  /** UUID of the parent HOA claim, when this claim is a unit-level child. */
  parent_claim_uuid?: string | null;
  /** Count of non-deleted child claims (set on HOA parent claims). */
  child_claim_count?: number;
  /** HOA community context (set on HOA umbrella claims only). */
  hoa_community?: {
    uuid: string;
    name: string;
    address: string;
    manager_name: string;
    manager_email: string;
    unit_claim_count: number;
  } | null;
  /** Contractor/installer surfaced read-only from the registration. */
  registration_contractor?: {
    business_name: string;
    subcontractor_name: string;
  } | null;
  attribute_values: Record<string, unknown>;
  pricing_guidance?: PricingGuidance | null;
  potential_duplicates?: PotentialDuplicateClaim[];
  duplicate_resolution?: DuplicateResolution | null;
  /** Duplicate-media findings. Null when the capability is off for the tenant. */
  media_matches?: ImageReview | null;
  created_at: string;
  updated_at: string;
}

/**
 * Whether the claim's current potential-duplicate matches have been
 * acknowledged. Computed live by the detail serializer: `resolved` is true
 * only when a reviewer signed off AND every current match was part of that
 * sign-off, so a sibling claim filed later re-surfaces the warning
 * (`new_match_count` > 0). Null when there are no matches to resolve.
 */
export interface DuplicateResolution {
  resolved: boolean;
  resolved_at: string | null;
  resolved_by_name: string;
  note: string;
  new_match_count: number;
}

/**
 * Sibling claims whose consumer matches this claim's consumer on the
 * tenant-configured refs (email / phone / name by default). Served by the
 * claim detail serializer, computed live - the detail page renders them
 * as a potential-duplicate callout with links.
 */
export interface PotentialDuplicateClaim {
  claim_uuid: string;
  reference: string;
  matched_on: string[];
  /** Readable value behind each ref in ``matched_on`` (e.g.
   *  ``consumer_email`` -> ``jane@example.com``), so the pop-up can surface a
   *  label-value pair (WPS-150) instead of a bare criterion label. Keyed by the
   *  same ref strings as ``matched_on``; a ref may be absent when no readable
   *  value resolved. */
  matched_values?: Record<string, string>;
  consumer_name: string;
  claim_state: string;
  created_at: string;
  /** Whether this specific sibling has already been acknowledged. Lets each
   *  duplicate be marked reviewed individually (UAT FR-006). */
  reviewed: boolean;
  /** Read-only review context (WPS-150 QA): whether the existing claim is still
   *  open, and its claim type + major defect where the tenant's schema carries
   *  them. Display only - these never participate in matching. */
  is_open?: boolean;
  claim_type?: string;
  claim_defect?: string;
  /** WPS-150 (Patrick QA): ordered, per-claim-type detail rows the reviewer
   *  compares (registration number, structure, installed date, major, product,
   *  colour, supplier, contact name/email, contractor contact). Each ``key`` is a
   *  semantic token the dialog maps to a localized label; blank rows are omitted
   *  server-side. */
  details?: Array<{ key: string; value: string }>;
}

/**
 * Receipt-pricing guidance served by the claim detail serializer when the
 * tenant has ``claims.pricing_guidance`` configured. Decision input for
 * whoever judges the amount: the product's price attributes (labels are
 * tenant-authored) plus whether THIS claim carries the receipt field.
 */
export interface PricingGuidance {
  prices: Array<{
    code_name: string;
    label: string;
    amount: number;
    currency: string | null;
  }>;
  receipt: { code_name: string; label: string; present: boolean } | null;
  currency: string | null;
}

/**
 * Phase 7 of the tenant-agnostic claims migration: backend-driven
 * claim-type options for the submit-claim wizard's Step 0. Returned by
 * ``GET /api/claims/claim-type-options/``.
 */
export interface ClaimTypeOption {
  value: string;
  label: string;
  description: string;
  icon: string;
  active: boolean;
  /** Whether this claim type requires a holding (registration). */
  requires_holding: boolean;
  /**
   * When true, choosing this type provisions its own property + registration
   * during claim submission - the wizard offers a "start the claim now" vs
   * "register the structure first" choice instead of a registration picker.
   */
  creates_registration?: boolean;
  /**
   * When set, the registration wizard will filter its product list to only
   * show products from the named product template (e.g. "Basement Finishing
   * System"). Empty string / absent means no filtering.
   */
  product_template_name?: string;
  /** True when this is an umbrella type (parent + child claims, e.g. HOA). */
  umbrella?: boolean;
  /** For umbrella types: the child claim type each item is filed as. */
  child_claim_schema_key?: string;
}

export interface WorkflowInstance {
  uuid: string;
  claim_uuid: string;
  workflow_uuid: string;
  workflow_name: string;
  current_state_uuid: string;
  current_state_key: string;
  current_state_label: string;
  current_state_color: string;
  current_state_editable_fields: string[] | null;
  /** Decision values an approval gate already judged - read-only (TA-55). */
  current_state_locked_fields?: string[];
  current_state_is_terminal: boolean;
  /**
   * Phase 2 EAV: per-state metadata (role label, workspace category,
   * banner / guidance copy, settlement gating). Keyed by
   * ``WorkflowStateAttribute.code_name``.
   */
  current_state_attribute_values?: Record<string, unknown>;
  /** WP1-67: coarse lifecycle stage of the current status (flag-gated). */
  current_stage?: {
    key: string;
    label: string;
    order: number | null;
    helper: string;
  };
  /** WP1-67: the current status with hover helper text (flag-gated). */
  current_status?: {
    key: string;
    label: string;
    color: string;
    helper: string;
  };
  /** WP1-67: reopened tag, derived from workflow history (flag-gated). */
  reopened?: boolean;
  reopen_count?: number;
}

export interface WorkflowAction {
  uuid: string;
  key: string;
  label: string;
  icon: string | null;
  applicability: string;
  actionable: boolean;
  source_state_uuid: string | null;
  source_state_key: string | null;
  target_state_uuid: string | null;
  target_state_key: string | null;
  /** The workflow's label for the destination state, for confirm copy. */
  target_state_label?: string | null;
  order: number;
  auto_proceed: boolean;
  prerequisites: unknown[] | null;
  side_effects: unknown[] | null;
  capture_fields: unknown[] | null;
  /** Config-authored panel keys the capture dialog renders (e.g. "pricing"). */
  guidance_panels?: string[];
  /** Config-authored prominence copy: render this action as a record callout. */
  banner?: string | null;
  /**
   * WPS-164: run this action directly from the action bar with no capture
   * dialog or confirmation window (e.g. Initiate Scrap).
   */
  execute_immediately?: boolean;
  /** True when this action already ran and the claim has not moved since. */
  performed?: boolean;
  /** Config-authored past-tense label shown once `performed` (WPS-175 #5). */
  performed_label?: string | null;
  /** Config-authored forward step (`ui_hints.variant: "primary"`). */
  primary?: boolean;
  /** Config-authored confirmation shown when the action succeeds. */
  success_message?: string | null;
  actor: "ADMIN" | "CONSUMER";
}

export interface EmailSent {
  uuid: string;
  recipient_email: string;
  recipient_name: string;
  recipient_kind: string;
  subject: string;
  template_slug: string;
  status: string;
  rendered_html: string;
  created_at: string | null;
}

export interface ActionLogEntry {
  uuid: string;
  action_key: string;
  action_label: string;
  action_icon: string | null;
  to_state_label: string | null;
  from_state_label: string | null;
  /**
   * This transition left a terminal state - i.e. the claim was reopened. The
   * timeline narrates destinations, so such an entry must be headlined by its
   * action ("Reopen Claim") or it reads as an ordinary move into the state it
   * returned to.
   */
  reverts_closure?: boolean;
  initiated_by: {
    type: "USER" | "SYSTEM";
    user: { uuid: string; email: string; name?: string } | null;
  };
  parameters: Record<string, unknown> | null;
  captured_values: Record<string, unknown> | null;
  emails_sent?: EmailSent[];
  /**
   * WPS-81: present when an authorised user overrode a block to run this
   * action. Echoes the single-use ExceptionGrant so the activity feed can
   * attribute the override (who / when / why).
   */
  override?: {
    reason_codes: string[];
    reason_labels: string[];
    comment: string;
    granted_by: string;
    granted_at: string | null;
  } | null;
  created_at: string;
}

export interface CycleTimeStageBucket {
  stage_key: string;
  stage_label: string;
  stage_order: number;
  internal_seconds: number;
  external_seconds: number;
  total_seconds: number;
}

export interface CycleTimeStatusBucket {
  status_key: string;
  status_label: string;
  internal_seconds: number;
  external_seconds: number;
  total_seconds: number;
}

export interface CycleTimeUser {
  user_id: number | null;
  name: string | null;
  internal_seconds: number;
}

/** WP1-67 cycle-time metrics for a claim. */
export interface CycleTime {
  total_seconds: number;
  internal_seconds: number;
  external_seconds: number;
  is_open: boolean;
  by_stage: CycleTimeStageBucket[];
  by_status: CycleTimeStatusBucket[];
  by_user: CycleTimeUser[];
}

export interface ClaimDetailResponse {
  claim: ClaimDetail;
  metadata: {
    workflow_instance: WorkflowInstance | null;
    /** WP1-67 cycle-time metrics (flag-gated; absent/null when off). */
    cycle_time?: CycleTime | null;
    available_actions: WorkflowAction[];
    editable_field_metadata?: ActionFieldMeta[];
    /** Workflow-level metadata (e.g. is_priority, sla_days for Job Stop). */
    workflow_metadata?: Record<string, unknown>;
    /** Owner-facing partner-branch summary (park banner + progress cards). */
    partner_branches?: {
      open_count: number;
      branches: {
        capability: string;
        capability_label: string;
        state_key: string;
        state_label: string;
        state_color: string;
        is_open: boolean;
        partner_org_name: string;
        partner_contact_email: string;
        fields: {
          code_name: string;
          label: string;
          field_type: string;
          configuration_data: Record<string, unknown>;
          value: unknown;
        }[];
      }[];
    };
    [key: string]: unknown;
  };
  history: ActionLogEntry[];
}

export interface ActionFieldMeta {
  code_name: string;
  display_name: string;
  field_type: string;
  required: boolean;
  configuration_data: Record<string, unknown>;
  /** Claim-derived prefill (e.g. currency from pricing guidance). */
  default_value?: unknown;
}

/**
 * A partner-submitted value shown read-only in an "approve as submitted"
 * sign-off dialog. The values are sourced server-side from the partner's
 * branch instance, so the owner reviews exactly what they are approving
 * rather than re-keying it. See ``ui_hints.review_submitted``.
 */
export interface SubmittedReviewField {
  code_name: string;
  label: string;
  field_type: string;
  configuration_data: Record<string, unknown>;
  value: unknown;
}

export interface ActionMetadataResponse {
  action: WorkflowAction;
  fields: ActionFieldMeta[];
  /**
   * When present, the action is a read-only sign-off: render these
   * partner-submitted values read-only instead of editable capture inputs.
   * ``fields`` is empty in this case.
   */
  submitted_review?: SubmittedReviewField[] | null;
  /**
   * WPS-81: present (non-null) when the action carries an
   * ``ExceptionActionBinding`` and cannot progress without a grant. Lets the
   * dialog render the why-blocked banner + override control on open, before
   * any execute attempt. Same wire shape as the execute error's ``result``.
   */
  exception?: {
    code: "exception_required";
    message: string;
    binding: ExceptionActionBinding | null;
  } | null;
}

export interface CreateClaimMetadataResponse {
  workflow: {
    uuid: string;
    name: string;
    description: string;
    states: Array<{
      uuid: string;
      key: string;
      label: string;
      color: string;
      is_terminal: boolean;
      is_initial: boolean;
      editable_fields: string[] | null;
    }>;
    actions: WorkflowAction[];
  };
  fields: ActionFieldMeta[];
  // Initial values for capture fields, derived server-side from the
  // registration (holding + consumer) via each field's
  // ``configuration_data.prefill_source``. Keyed by field code_name; only
  // fields with a resolved value appear. ``{}`` for holdingless claim types.
  prefill: Record<string, unknown>;
}

// ── Hooks ────────────────────────────────────────────────

/** List claims (paginated). ``filter_options`` carries the record-metadata
 *  facets for the filter fly-out (see backend ``get_filter_options``). */
export function useClaims(params?: Record<string, string>) {
  return useQuery<{
    results: ClaimRow[];
    count: number;
    filter_options?: ProductFilterFacet[];
    sortable_columns?: SortableColumn[];
  }>({
    queryKey: ["claims", params],
    queryFn: () => api.get("/claims/claims/", { params }).then((r) => r.data),
  });
}

/** Invalidate every claims-derived query after a mutation.
 *
 *  The pill counts and the nav badge live under their OWN top-level keys (so
 *  they get the COUNTS cache policy rather than LIST_LIVE). invalidateQueries
 *  matches by prefix, so `['claims']` does NOT reach them - without this helper
 *  a state change would refresh the list while leaving the pills and badge
 *  stale. Every claims mutation must call this instead of invalidating
 *  `['claims']` directly. */
export function invalidateClaimsQueries(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["claims"] });
  qc.invalidateQueries({ queryKey: ["claims-pill-counts"] });
  qc.invalidateQueries({ queryKey: ["claims-assigned-badge"] });
}

/** WPS-142 #7: a claim's follow-up auto-advance runs ASYNCHRONOUSLY (a Pulse
 *  workflow-action binding delivered via Cloud Tasks) and commits AFTER the
 *  triggering action's HTTP response has already returned, so the mounted claim
 *  detail page (which never polls) keeps showing the pre-advance stage until a
 *  manual reload - the "I had to refresh repeatedly" the OC tester reported.
 *  Delegates to the shared bounded re-poll; on advance it refreshes the
 *  list-derived claim queries + the dynamic checklist. */
export function scheduleClaimStateRefresh(
  qc: QueryClient,
  uuid: string,
  previousStateKey: string | null | undefined,
): void {
  scheduleResourceStateRefresh(qc, {
    detailQueryKey: ["claim", uuid],
    previousStateKey,
    selectStateKey: (data) =>
      (data as ClaimDetailResponse | undefined)?.metadata?.workflow_instance
        ?.current_state_key ?? null,
    onStateAdvanced: () => {
      invalidateClaimsQueries(qc);
      qc.invalidateQueries({ queryKey: ["claim-checklist", uuid] });
    },
  });
}

/** Status-bucket counts for the claims list pills.
 *
 *  Aggregated server-side (one GROUP BY). This replaced tallying the pills from
 *  a `page_size=250` claims download grouped in the browser, which reported
 *  counts for only the first 250 claims - wrong for any tenant past that, and a
 *  payload that grew with the tenant. Mirrors the registrations equivalent. */
export function useClaimStatusCounts(params?: Record<string, string>) {
  return useQuery<ClaimStatusCount[]>({
    queryKey: ["claims-status-counts", params],
    queryFn: () =>
      api.get("/claims/claims/status-counts/", { params }).then((r) => r.data),
  });
}

/** Unfiltered claim TOTAL, for the header and the table's pagination. Only
 *  `count` is read, so it asks for a single row rather than a page of them.
 *  Own query key so it lands on the COUNTS policy, not LIST_LIVE. */
export function useClaimsTotalCount(params?: Record<string, string>) {
  return useQuery<{ results: ClaimRow[]; count: number }>({
    queryKey: ["claims-pill-counts", params],
    queryFn: () => api.get("/claims/claims/", { params }).then((r) => r.data),
  });
}

/** Does the current user have any claims assigned? Drives the nav badge only.
 *  Its own query key so it lands on the COUNTS policy instead of LIST_LIVE -
 *  a nav badge must not re-hit the claims list on every navigation. */
export function useMyAssignedClaimsBadge(enabled: boolean) {
  return useQuery<{ count: number }>({
    queryKey: ["claims-assigned-badge"],
    queryFn: () =>
      api
        .get("/claims/claims/", {
          params: { assigned_to: "me", page_size: "1" },
        })
        .then((r) => r.data),
    enabled,
  });
}

/** Get claim detail with metadata + history. */
export function useClaim(uuid: string | null) {
  return useQuery<ClaimDetailResponse>({
    queryKey: ["claim", uuid],
    queryFn: () => api.get(`/claims/claims/${uuid}/`).then((r) => r.data),
    enabled: !!uuid,
  });
}

/** A single claim's pricing guidance (with/without-receipt reference prices) -
 *  lightweight, so the assessment editor can show a child's reference pricing. */
export function usePricingGuidance(uuid: string | null) {
  return useQuery<PricingGuidance | null>({
    queryKey: ["pricing-guidance", uuid],
    queryFn: () =>
      api
        .get(`/claims/claims/${uuid}/pricing-guidance/`)
        .then((r) => (r.data?.data ?? r.data)?.pricing_guidance ?? null),
    enabled: !!uuid,
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch metadata for executing an action (fields to capture). */
export function useClaimActionMetadata() {
  return useMutation<
    ActionMetadataResponse,
    Error,
    { uuid: string; action_key: string }
  >({
    mutationFn: ({ uuid, action_key }) =>
      api
        .post(`/claims/claims/${uuid}/workflow/action/metadata/`, {
          action_key,
        })
        .then((r) => r.data),
  });
}

/** Execute a workflow action on a claim. */
export function useExecuteClaimAction() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    {
      uuid: string;
      action_key: string;
      parameters?: Record<string, unknown>;
      captured_values?: Record<string, unknown>;
      /** Single-use ExceptionGrant uuid to override a block / satisfy a binding. */
      exception_grant_id?: string;
    }
  >({
    mutationFn: ({ uuid, ...body }) =>
      api
        .post(`/claims/claims/${uuid}/workflow/action/execute/`, body)
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      // Capture the stage BEFORE invalidating so the bounded re-poll below can
      // tell when an asynchronous follow-up auto-advance has landed (WPS-142 #7).
      const previousStateKey =
        qc.getQueryData<ClaimDetailResponse>(["claim", vars.uuid])?.metadata
          ?.workflow_instance?.current_state_key ?? null;
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
      // The dynamic review checklist is resolved live from the claim's current
      // defect (major/minor); refresh it so an action that changes the defect
      // updates the Lab-tab checklist without a reload (WPS-59).
      qc.invalidateQueries({ queryKey: ["claim-checklist", vars.uuid] });
      // WPS-142 #7: the action's follow-up auto-advance may commit asynchronously
      // after this response; re-poll the detail until the stage changes so the
      // page reflects it without a manual reload.
      scheduleClaimStateRefresh(qc, vars.uuid, previousStateKey);
    },
  });
}

/** Direct edit of claim attributes (admin - bypasses workflow constraints). */
export function useEditClaimDirect() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    {
      uuid: string;
      attribute_values: Record<string, unknown>;
      edit_reason: string;
    }
  >({
    mutationFn: ({ uuid, attribute_values, edit_reason }) =>
      api
        .put(`/claims/claims/${uuid}/`, { attribute_values, edit_reason })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
      // Editing the defect (major/minor) must refresh the live checklist so the
      // Lab-tab photo categories track the new defect (WPS-59).
      qc.invalidateQueries({ queryKey: ["claim-checklist", vars.uuid] });
    },
  });
}

/** Edit claim fields in the current workflow state with audit reason. */
export function useEditClaimFields() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    {
      uuid: string;
      attribute_values: Record<string, unknown>;
      edit_reason: string;
    }
  >({
    mutationFn: ({ uuid, attribute_values, edit_reason }) =>
      api
        .post(`/claims/claims/${uuid}/workflow/edit/`, {
          attribute_values,
          edit_reason,
        })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
      // Keep the live checklist in step with an in-state edit of the defect
      // (major/minor) so the Lab-tab checklist updates dynamically (WPS-59).
      qc.invalidateQueries({ queryKey: ["claim-checklist", vars.uuid] });
    },
  });
}

/**
 * Phase 7 of the tenant-agnostic claims migration: claim-type
 * taxonomy + icons + descriptions for the submit-claim wizard.
 * Returned options are tenant-configured via the seed (or admin) on
 * the ``claim_type`` ClaimAttribute SELECT.
 */
// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function claimTypeOptionsQueryOptions() {
  return queryOptions<{ options: ClaimTypeOption[] }>({
    queryKey: ["claim-type-options"],
    queryFn: () => api.get("/claims/claim-type-options/").then((r) => r.data),
    staleTime: 5 * 60 * 1000, // config-time data
  });
}

export function useClaimTypeOptions() {
  return useQuery<{ options: ClaimTypeOption[] }>(
    claimTypeOptionsQueryOptions(),
  );
}

/**
 * Tenant-authored claim disclaimers keyed by context (e.g. `claim_submit`).
 * Display copy + acknowledgement config; rendered by `<DisclaimerAck>`.
 */
// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function claimDisclaimersQueryOptions() {
  return queryOptions<{ disclaimers: Record<string, DisclaimerConfig> }>({
    queryKey: ["claim-disclaimers"],
    queryFn: () =>
      api.get("/claims/disclaimers/").then((r) => r.data?.result ?? r.data),
    staleTime: 5 * 60 * 1000,
  });
}

export function useClaimDisclaimers() {
  return useQuery<{ disclaimers: Record<string, DisclaimerConfig> }>(
    claimDisclaimersQueryOptions(),
  );
}

/**
 * Get creation form metadata, keyed by ``(claimSchemaKey,
 * productTemplateName)`` rather than by holding.
 *
 * The backend response is a pure function of the tenant, the resolved
 * workflow/schema, and the product template *name* (the only holding-derived
 * input - it narrows the ``product_impacted`` option list). Two holdings on
 * the same schema + template return byte-identical metadata, so keying by
 * template name means selecting a second holding on the same line is an
 * instant cache hit, and the post-login warmer can prefetch one entry per
 * (schema, template) pair.
 *
 * Always pass ``claimSchemaKey`` so the backend resolves the correct workflow
 * for the selected claim type. Then supply *either*:
 *   - ``productTemplateName`` (preferred - cache-friendly, keys per template), or
 *   - ``holdingUuid`` (fallback for callers that only have a holding and not
 *     its template name; the backend derives the template from the holding).
 * Both are optional (holdingless claim types have neither).
 *
 * Exported as shared query options so the warmer reuses this exact
 * key/queryFn (no drift).
 */
export function createClaimMetadataQueryOptions(
  claimSchemaKey: string | null | undefined,
  opts?: { productTemplateName?: string | null; holdingUuid?: string | null },
) {
  const productTemplateName = opts?.productTemplateName ?? null;
  const holdingUuid = opts?.holdingUuid ?? null;
  const params: Record<string, string> = {};
  if (claimSchemaKey) params.claim_schema_key = claimSchemaKey;
  // Send ``holding_uuid`` whenever we have it. The backend resolves the workflow
  // from ``claim_schema_key`` (when present) and uses the holding only to (a)
  // narrow the ``product_impacted`` options and (b) resolve registration-derived
  // prefill values - name / phone / email / mailing address / install date
  // (WPS-144). The cache-friendly ``product_template_name`` path cannot provide
  // prefill (it has no holding), so it is only the fallback when no holding is
  // known (e.g. the cache warmer, or holdingless claim types). Without a schema
  // key the backend needs the holding uuid to resolve the workflow; sending
  // template-name alone 400s.
  if (holdingUuid) {
    params.holding_uuid = holdingUuid;
  } else if (productTemplateName) {
    params.product_template_name = productTemplateName;
  }

  return queryOptions<CreateClaimMetadataResponse>({
    queryKey: [
      "create-claim-metadata",
      claimSchemaKey ?? null,
      // Discriminate the cache entry by what actually resolves the workflow.
      // When we send ``holding_uuid`` (no schema key), keying by template name
      // would let two different holdings that share a template collide on one
      // entry and serve each other's stale metadata; key by the holding uuid
      // instead. When ``product_template_name`` is the resolving param (schema
      // key present), keep the template key so it stays cacheable across
      // holdings of the same template.
      params.holding_uuid
        ? `holding:${params.holding_uuid}`
        : (productTemplateName ?? null),
    ],
    queryFn: () =>
      api.get("/claims/create-claim-metadata/", { params }).then((r) => r.data),
    enabled: !!claimSchemaKey || !!holdingUuid,
  });
}

export function useCreateClaimMetadata(
  claimSchemaKey?: string | null,
  opts?: { productTemplateName?: string | null; holdingUuid?: string | null },
) {
  return useQuery<CreateClaimMetadataResponse>(
    createClaimMetadataQueryOptions(claimSchemaKey, opts),
  );
}

/**
 * Create a claim. Posts to the generic claim-list endpoint
 * (``ClaimListCreateView.post``) which delegates to
 * ``create_claim_for_holding`` - that service handles workflow
 * resolution, schema validation, ACL setup, and workflow-instance
 * initialisation. The legacy ``/claim-workflows/create-claim/``
 * route was removed in Phase 1 of the tenant-agnostic claims
 * migration; the underlying service is the same one it called.
 *
 * Backend expects ``attribute_values`` (not ``captured_values``) -
 * the hook accepts ``captured_values`` for caller ergonomics
 * (matches the workflow-action terminology used elsewhere in the
 * wizard) and translates on the wire.
 */
export function useCreateClaim() {
  const qc = useQueryClient();
  return useMutation<
    ClaimDetail,
    Error,
    {
      holding_uuid?: string | null;
      claim_schema_key?: string | null;
      captured_values: Record<string, unknown>;
    }
  >({
    mutationFn: ({ holding_uuid, claim_schema_key, captured_values }) => {
      const body: Record<string, unknown> = {
        attribute_values: captured_values,
      };
      if (holding_uuid) body.holding_uuid = holding_uuid;
      if (claim_schema_key) body.claim_schema_key = claim_schema_key;
      return api.post("/claims/claims/", body).then((r) => r.data);
    },
    onSuccess: () => {
      invalidateClaimsQueries(qc);
    },
  });
}

// ── Multi-item (register + claim in one step) ────────────

export interface MultiItemClaimType {
  key: string;
  label: string;
  child_claim_schema_key: string;
}

/**
 * The tenant's umbrella (multi-item) claim types, sourced from config and
 * gated on the `multi_item_claims` capability. Drives the single-vs-multi
 * fork off the flag - not off claim_type options (so it works for tenants
 * that don't model a claim_type attribute). Pass `enabled` = the flag.
 */
// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function multiItemClaimTypesQueryOptions() {
  return queryOptions<{ types: MultiItemClaimType[] }>({
    queryKey: ["multi-item-claim-types"],
    queryFn: () =>
      api
        .get("/claims/multi-item-claim-types/")
        .then((r) => r.data?.result ?? r.data),
  });
}

export function useMultiItemClaimTypes(enabled = true) {
  return useQuery<{ types: MultiItemClaimType[] }>({
    ...multiItemClaimTypesQueryOptions(),
    enabled,
  });
}

export interface RegisterAndClaimLine {
  product_uuid: string;
  quantity?: number;
  registration_field_values?: Record<string, unknown>;
  attribute_values?: Record<string, unknown>;
}

export interface RegisterAndClaimPayload {
  claim_schema_key: string;
  consumer: {
    email: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    /** Canonical `{display, value}` address envelope (DL-83). */
    address?: { display: string; value: Record<string, string> };
  };
  lines: RegisterAndClaimLine[];
  parent_attribute_values?: Record<string, unknown>;
  shared_attribute_values?: Record<string, unknown>;
}

export interface RegisterAndClaimResult {
  created: number;
  results: Array<{
    row: number;
    holding_uuid: string;
    claim_uuid: string;
    claim_number: string;
  }>;
  parent_uuid?: string;
  parent_claim_number?: string;
}

/**
 * Register each line item AND file its claim in one all-or-nothing request,
 * under one umbrella parent (when the claim type is configured as an
 * umbrella). The single-step multi-item flow - posts to
 * ``RegisterAndClaimView``. The response envelope nests under ``result``.
 */
export function useRegisterAndClaim() {
  const qc = useQueryClient();
  return useMutation<RegisterAndClaimResult, Error, RegisterAndClaimPayload>({
    mutationFn: (payload) =>
      api
        .post("/claims/register-and-claim/", payload)
        .then((r) => r.data?.result ?? r.data),
    onSuccess: () => {
      invalidateClaimsQueries(qc);
      qc.invalidateQueries({ queryKey: ["holdings"] });
    },
  });
}

/** Toggle the flag on a claim. */
export function useToggleClaimFlag() {
  const qc = useQueryClient();
  return useMutation<
    { is_flagged: boolean; flag_reason: string; flagged_at: string | null },
    Error,
    { uuid: string; reason?: string }
  >({
    mutationFn: ({ uuid, reason }) =>
      api
        .post(`/claims/claims/${uuid}/flag/`, { reason: reason || "" })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

/** Assign or reassign a claim to a user. Pass null to unassign. */
export function useAssignClaim() {
  const qc = useQueryClient();
  return useMutation<
    {
      assigned_to: { uuid: string; name: string } | null;
      assigned_at: string | null;
    },
    Error,
    { uuid: string; assigned_to_uuid: string | null }
  >({
    mutationFn: ({ uuid, assigned_to_uuid }) =>
      api
        .post(`/claims/claims/${uuid}/assign/`, { assigned_to_uuid })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

export interface AssignablePartner {
  partnership_uuid: string;
  org_name: string;
  org_uuid: string;
  role_name: string;
  capability: string;
  capability_label: string;
}

/** Partner + capability entries assignable to a claim (assignee-dropdown Partners group). */
export function useClaimAssignablePartners(claimUuid: string) {
  return useQuery<AssignablePartner[]>({
    queryKey: ["claim-assignable-partners", claimUuid],
    enabled: !!claimUuid,
    queryFn: () =>
      api
        .get(`/claims/claims/${claimUuid}/assignable-partners/`)
        .then((r) => r.data?.result ?? r.data ?? []),
  });
}

/** Assign a claim to a partner for a capability - forks the partner branch + parks the claim. */
export function useAssignClaimToPartner(claimUuid: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { partnership_uuid: string; capability: string }
  >({
    mutationFn: (body) =>
      api
        .post(`/claims/claims/${claimUuid}/assign-partner/`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", claimUuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

/**
 * Acknowledge (or re-open) the potential-duplicate matches on a claim.
 * Pass `reopen: true` to clear a prior resolution; otherwise the current
 * matches are snapshotted and signed off with an optional note.
 */
export type {
  ImageReview,
  ImageReviewMatch,
  ImageReviewOutcome,
} from "@/admin/components/claims/duplicate-media-panel";


/**
 * Record a reviewer's verdict on one duplicate-media finding.
 *
 * Posts the chosen OUTCOME, not an acknowledgement - what the reviewer
 * concluded is the signal that lets the platform measure its own accuracy.
 */
export function useResolveMediaMatch() {
  const qc = useQueryClient();
  return useMutation<
    ImageReview | null,
    Error,
    { uuid: string; matchUuid: string; outcome: string; note?: string }
  >({
    mutationFn: ({ uuid, matchUuid, outcome, note }) =>
      api
        .post(`/claims/claims/${uuid}/media-matches/${matchUuid}/resolve/`, {
          outcome,
          note: note || "",
        })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

export function useResolveClaimDuplicates() {
  const qc = useQueryClient();
  return useMutation<
    {
      potential_duplicates?: PotentialDuplicateClaim[];
      duplicate_resolution: DuplicateResolution | null;
    },
    Error,
    {
      uuid: string;
      note?: string;
      reopen?: boolean;
      /** Acknowledge just this one sibling (UAT FR-006). Omit to mark all. */
      duplicateClaimUuid?: string;
    }
  >({
    mutationFn: ({ uuid, note, reopen, duplicateClaimUuid }) =>
      api
        .post(`/claims/claims/${uuid}/resolve-duplicates/`, {
          note: note || "",
          reopen: reopen ?? false,
          duplicate_claim_uuid: duplicateClaimUuid ?? null,
        })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

/**
 * WPS-150 - create-time potential-duplicate pre-check. Runs the existing
 * duplicate-claim matcher against a holding BEFORE a claim is saved, so the
 * submit wizard can surface an informational warning pop-up. Returns [] when
 * the `duplicates` flag is off for the tenant.
 */
export function useCheckClaimDuplicates() {
  return useMutation<
    PotentialDuplicateClaim[],
    Error,
    {
      holdingUuid?: string | null;
      claimSchemaKey?: string | null;
      /** WPS-150 (Patrick QA): captured claim-attribute values for the
       *  holdingless (uninstalled / job-stop) pre-check - supplier / product /
       *  colour, or the job-stop property address. Ignored when a holding is
       *  passed (the holding supplies those). */
      capturedValues?: Record<string, unknown>;
    }
  >({
    mutationFn: ({ holdingUuid, claimSchemaKey, capturedValues }) =>
      api
        .post("/claims/potential-duplicates/", {
          holding_uuid: holdingUuid ?? undefined,
          claim_schema_key: claimSchemaKey ?? undefined,
          captured_values: capturedValues ?? undefined,
        })
        .then((r) => r.data?.potential_duplicates ?? []),
  });
}

/**
 * WPS-150 - registration-time variant of the duplicate pre-check for flows that
 * create the holding server-side (register-and-claim / multi-item claims).
 * There is no holding to check yet, so it matches existing claims from the
 * registration fields: the shared consumer and the products being registered.
 * Returns [] when the `duplicates` flag is off for the tenant.
 */
export function useCheckRegistrationClaimDuplicates() {
  return useMutation<
    PotentialDuplicateClaim[],
    Error,
    {
      consumer: {
        email?: string;
        first_name?: string;
        last_name?: string;
        phone?: string;
        address?: unknown;
      };
      productUuids?: string[];
    }
  >({
    mutationFn: ({ consumer, productUuids }) =>
      api
        .post("/claims/potential-duplicates/", {
          consumer,
          product_uuids: productUuids ?? [],
        })
        .then((r) => r.data?.potential_duplicates ?? []),
  });
}

/** Set the triage labels on a claim (lightweight, no edit reason). */
export function useSetClaimLabels() {
  const qc = useQueryClient();
  return useMutation<
    { labels: string[] },
    Error,
    { uuid: string; labels: string[] }
  >({
    mutationFn: ({ uuid, labels }) =>
      api
        .post(`/claims/claims/${uuid}/labels/`, { labels })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

export interface ClaimLabelOption {
  value: string;
  label: string;
  color?: string | null;
  /** False for a retired label: rendered on tagged claims, never offered. */
  active?: boolean;
}

/**
 * Triage label options (from the `labels` attribute registry), retired ones
 * included for chip display. Offer only `selectableLabelOptions(...)`.
 */
export function useClaimLabelOptions() {
  return useQuery<ClaimLabelOption[]>({
    queryKey: ["claim-label-options"],
    queryFn: () =>
      api
        .get<{ options: ClaimLabelOption[] }>("/claims/label-options/")
        .then((r) => r.data.options ?? []),
    staleTime: 5 * 60 * 1000,
  });
}

/** Append an uploaded file to claim evidence. */
export function useAddClaimEvidence() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { uuid: string; file_uuid: string; field_code?: string }
  >({
    mutationFn: ({ uuid, file_uuid, field_code }) =>
      api
        .post(`/claims/claims/${uuid}/evidence/`, { file_uuid, field_code })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
      // Adding evidence can auto-complete an open "additional photos" lab
      // request and satisfy checklist items — refresh both so the pending
      // badge + "awaiting documents" banner clear without a manual reload.
      qc.invalidateQueries({ queryKey: ["lab-requests", vars.uuid] });
      qc.invalidateQueries({ queryKey: ["claim-checklist", vars.uuid] });
    },
  });
}

/** Remove a file from claim evidence (WP1-63). */
export function useRemoveClaimEvidence() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { uuid: string; file_uuid: string }>({
    mutationFn: ({ uuid, file_uuid }) =>
      api
        .delete(`/claims/claims/${uuid}/evidence/${file_uuid}/`)
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
      // Keep the checklist card in sync with what's now attached.
      qc.invalidateQueries({ queryKey: ["claim-checklist", vars.uuid] });
    },
  });
}

/** Rename a claim evidence file's display name (WP1-63). */
export function useRenameClaimEvidenceFile() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { uuid: string; file_uuid: string; file_name: string }
  >({
    mutationFn: ({ uuid, file_uuid, file_name }) =>
      api
        .patch(`/claims/claims/${uuid}/evidence/${file_uuid}/`, { file_name })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

export function useContactClaimCustomer() {
  const qc = useQueryClient();
  return useMutation<
    { message: string },
    Error,
    { uuid: string; message: string }
  >({
    mutationFn: ({ uuid, message }) =>
      api
        .post(`/claims/claims/${uuid}/contact-customer/`, { message })
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim", vars.uuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

// ── HOA parent/child claims ──────────────────────────────

/** List the child (unit-level) claims for an HOA parent claim. */
export function useClaimChildren(parentUuid: string | null) {
  return useQuery<ClaimRow[]>({
    queryKey: ["claim-children", parentUuid],
    queryFn: () =>
      api
        .get(`/claims/claims/${parentUuid}/children/`)
        .then((r) => r.data?.data ?? r.data?.results ?? r.data ?? []),
    enabled: !!parentUuid,
  });
}

/** Create a child unit claim under an HOA parent claim. */
export function useCreateChildClaim() {
  const qc = useQueryClient();
  return useMutation<
    ClaimDetail,
    Error,
    {
      parentUuid: string;
      holding_uuid: string;
      claim_schema_key?: string;
      attribute_values: Record<string, unknown>;
    }
  >({
    mutationFn: ({ parentUuid, ...body }) =>
      api
        .post(`/claims/claims/${parentUuid}/create-child/`, body)
        .then((r) => r.data?.data ?? r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim-children", vars.parentUuid] });
      qc.invalidateQueries({ queryKey: ["claim", vars.parentUuid] });
      invalidateClaimsQueries(qc);
    },
  });
}

/** Send the same message to every child claim's consumer (HOA bulk email). */
export function useBulkEmailChildren() {
  return useMutation<
    { sent_count: number; skipped_count: number },
    Error,
    { parentUuid: string; message: string }
  >({
    mutationFn: ({ parentUuid, message }) =>
      api
        .post(`/claims/claims/${parentUuid}/bulk-email/`, { message })
        .then((r) => r.data?.data ?? r.data),
  });
}

/** Fetch organisation members for assignment dropdown. */
export interface OrgMember {
  uuid: string;
  user_uuid: string;
  user_email: string;
  user_name: string;
  role_name: string;
}

export function useOrgMembers(orgUuid: string | null) {
  return useQuery<OrgMember[]>({
    queryKey: ["org-members", orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/members/`)
        .then((r) => r.data?.results ?? r.data ?? []),
    enabled: !!orgUuid,
  });
}

// ── Claim Contacts ───────────────────────────────────────
//
// Replaces the old "watchers" concept: every party who should follow a
// claim (internal team member or external party) is now a Claim Contact,
// mirroring the same `/claims/claims/${uuid}/...` base path the watcher
// hooks used. Internal contacts wrap an org user; external contacts carry
// their own name/email/phone plus a `permission` (view_only | collaborator)
// controlling any portal access they get. Roles are a tenant-managed,
// freeform, reusable list (`/claims/claim-contact-roles/`).

export interface ClaimContact {
  uuid: string;
  user: string | null;
  external_email: string;
  external_name: string;
  external_phone: string;
  role: string | null;
  role_label: string;
  permission: "view_only" | "collaborator";
  permission_label: string;
  added_by: string | null;
  added_by_name: string | null;
  display_name: string;
  display_email: string;
  display_phone: string;
  is_internal: boolean;
  has_portal_access: boolean;
  is_main_contact: boolean;
  created_at: string;
}

export interface ClaimContactRole {
  uuid: string;
  label: string;
  value: string;
  is_active: boolean;
}

export function useClaimContacts(claimUuid: string | null) {
  return useQuery<ClaimContact[]>({
    queryKey: ["claim-contacts", claimUuid],
    queryFn: () =>
      api
        .get(`/claims/claims/${claimUuid}/contacts/`)
        .then((r) => r.data?.results ?? r.data ?? []),
    enabled: !!claimUuid,
  });
}

export function useAddClaimContact() {
  const qc = useQueryClient();
  return useMutation<
    ClaimContact,
    Error,
    {
      claimUuid: string;
      user?: string | null;
      external_email?: string;
      external_name?: string;
      external_phone?: string;
      permission?: string;
      role?: string | null;
      role_label?: string;
      is_main_contact?: boolean;
    }
  >({
    mutationFn: ({ claimUuid, ...body }) =>
      api
        .post(`/claims/claims/${claimUuid}/contacts/`, body)
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim-contacts", vars.claimUuid] });
    },
  });
}

export function useUpdateClaimContact() {
  const qc = useQueryClient();
  return useMutation<
    ClaimContact,
    Error,
    {
      claimUuid: string;
      contactUuid: string;
      external_email?: string;
      external_name?: string;
      external_phone?: string;
      permission?: string;
      role?: string | null;
      role_label?: string;
      is_main_contact?: boolean;
    }
  >({
    mutationFn: ({ claimUuid, contactUuid, ...body }) =>
      api
        .patch(`/claims/claims/${claimUuid}/contacts/${contactUuid}/`, body)
        .then((r) => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim-contacts", vars.claimUuid] });
    },
  });
}

export function useRemoveClaimContact() {
  const qc = useQueryClient();
  return useMutation<void, Error, { claimUuid: string; contactUuid: string }>({
    mutationFn: ({ claimUuid, contactUuid }) =>
      api
        .delete(`/claims/claims/${claimUuid}/contacts/${contactUuid}/`)
        .then(() => undefined),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["claim-contacts", vars.claimUuid] });
    },
  });
}

export function useClaimContactRoles() {
  return useQuery<ClaimContactRole[]>({
    queryKey: ["claim-contact-roles"],
    queryFn: () =>
      api
        .get("/claims/claim-contact-roles/")
        .then((r) => r.data?.results ?? r.data ?? []),
  });
}

export function useCreateClaimContactRole() {
  const qc = useQueryClient();
  return useMutation<ClaimContactRole, Error, { label: string }>({
    mutationFn: (body) =>
      api.post("/claims/claim-contact-roles/", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim-contact-roles"] });
    },
  });
}

// ── TEST/DEV affordance ──────────────────────────────────
//
// `useTestVAIntake` fires a test Voice Agent claim through the admin
// endpoint `POST /api/claims/test-va-intake/`, which drives the SAME
// intake pipeline the real Voice Agent uses - but with admin session
// auth, so no public API key is embedded in the frontend. Safe to remove
// with the "Test VA" button in `claims-page.tsx` and the backend
// `apps.claims.views.test_intake` module.

export interface TestVAIntakeResult {
  claim_uuid: string;
  claim_reference: string;
  holding_uuid: string;
  consumer_created: boolean;
  unidentified_product: boolean;
  current_state_key: string | null;
}

// Optional caller-supplied consumer. Blank fields fall back to a throwaway
// test consumer server-side; pass `email` to route the confirmation +
// evidence/upload-link emails to your own inbox.
export interface TestVAIntakePayload {
  email?: string;
  first_name?: string;
  last_name?: string;
  /** What the caller said about their product, in their own words. */
  product_query?: string;
  /**
   * Discrete answers the channel already collects, keyed by the tenant's
   * own attribute `code_name`s (whichever are flagged as identification
   * facets). These narrow the catalogue before any free-text matching runs.
   */
  product_facets?: Record<string, string>;
}

/**
 * One identification facet this tenant configures, as the dialog renders it.
 *
 * `code_name` is the key `product_facets` is sent under; `display_name` is the
 * tenant's own label for the attribute. Both come from the tenant's product
 * attribute config, so nothing about any tenant's catalogue is known here.
 */
export interface TestVAFacet {
  code_name: string;
  display_name: string;
  field_type: string;
}

/**
 * The facets the Test VA dialog builds its product fields from.
 *
 * LIST_LIVE, not REFERENCE: flagging an attribute as a facet is a server-side
 * config change the client never sees, so an empty list cached before it was
 * switched on would persist and keep telling the tester there is nothing to
 * narrow by.
 */
export function useTestVAFacets(enabled: boolean) {
  return useQuery<{ facets: TestVAFacet[] }>({
    queryKey: ["test-va-facets"],
    queryFn: () => api.get("/claims/test-va-intake/").then((r) => r.data),
    enabled,
  });
}

export function useTestVAIntake() {
  const qc = useQueryClient();
  return useMutation<TestVAIntakeResult, Error, TestVAIntakePayload | void>({
    mutationFn: (payload) =>
      api.post("/claims/test-va-intake/", payload ?? {}).then((r) => r.data),
    onSuccess: () => {
      invalidateClaimsQueries(qc);
    },
  });
}

/**
 * WPS-119 - "is this property already registered?"
 *
 * For claim types that provision their own registration on submit (BFS /
 * SunSuite), the RS enters the owner and address rather than picking a
 * registration. Without this the wizard would create a second registration
 * every time a claim is filed on a property already on file.
 *
 * Which fields constitute "the same property" is tenant config, not code -
 * the backend reads `registration_match_refs` off the claim-type option. OC's
 * rule is owner name + owner phone + address (which covers the postcode).
 * ALL must resolve to the same registration; the backend enforces that.
 *
 * `matched: false` is a normal, successful answer - a claim on a brand-new
 * property is the common case, not an error.
 */
export interface MatchingRegistration {
  matched: boolean
  matched_on: string[]
  partial: string[]
  holding_uuid?: string | null
  registration_number?: string | null
  owner_name?: string | null
  owner_phone?: string | null
  product_name?: string | null
  registration_status?: Record<string, unknown> | null
  structure_values?: Record<string, unknown> | null
  /** Null on this code path - only the batch address matcher annotates them. */
  installation_date?: string | null
  claim_reference?: string | null
}

export function useMatchingRegistration() {
  return useMutation<
    MatchingRegistration,
    unknown,
    { claimSchemaKey: string; capturedValues: Record<string, unknown> }
  >({
    mutationFn: ({ claimSchemaKey, capturedValues }) =>
      api
        .post("/claims/matching-registration/", {
          claim_schema_key: claimSchemaKey,
          captured_values: capturedValues,
        })
        .then(
          (r) =>
            (r.data as MatchingRegistration | undefined) ?? {
              matched: false,
              matched_on: [],
              partial: [],
            },
        ),
  });
}
